import type {
	ProntuarioPastSessionRecord,
	ProntuarioPastSessionTask,
} from '../types/prontuarioPastSession';
import type { ProntuarioPresenceStatus } from '../constants/prontuarioSessionChoices';
import { formatProntuarioSessionDate } from './formatProntuarioSessionDate';
import { isMedicalRecordSessionPostProcessingPending } from '@/utils/medicalRecordSessionPostProcessing';

const ATTENDANCE_TO_PRESENCE: Record<string, ProntuarioPresenceStatus> = {
	PRESENT: 'PRESENTE',
	ABSENT_JUSTIFIED: 'FALTOU',
	ABSENT_UNJUSTIFIED: 'FALTOU',
};

const RISK_LABELS: Record<string, string> = {
	NONE: 'Nenhum',
	LOW: 'Baixo',
	MODERATE: 'Moderado',
	HIGH: 'Alto',
};

const mapAttendance = (
	attendance?: string | null,
): ProntuarioPresenceStatus | undefined =>
	attendance ? ATTENDANCE_TO_PRESENCE[attendance] : undefined;

const mapOptionalBoolean = (value: boolean | null): string | undefined => {
	if (value === true) return 'Sim';
	if (value === false) return 'Não';
	return undefined;
};

const mapDone = (done: boolean | null): string => {
	if (done === true) return 'Sim';
	if (done === false) return 'Não';
	return 'Pendente';
};

export const mapMedicalRecordSessionTasks = (
	tasks?: MedicalRecords.MedicalRecordSessionTask[],
): ProntuarioPastSessionTask[] =>
	(tasks ?? []).map((task) => ({
		id: task.id,
		description: task.description,
		doneLabel: mapDone(task.done),
		hadDifficultyLabel: mapOptionalBoolean(task.had_difficulty),
		enjoyedLabel: mapOptionalBoolean(task.enjoyed),
		engagementRating: task.engagement_rating ?? undefined,
		reviewNotes: task.review_notes?.trim() || undefined,
	}));

const mapRiskFields = (
	recordSession: MedicalRecords.MedicalRecordSession,
): Pick<
	ProntuarioPastSessionRecord,
	'indicadorRisco' | 'observacoesRisco' | 'observacoesRiscoTag'
> => {
	const riskNotes = recordSession.risk_notes?.trim();
	const riskIndicator = recordSession.risk_indicator;
	const riskLabel =
		riskIndicator && riskIndicator !== 'NONE'
			? RISK_LABELS[riskIndicator]
			: undefined;

	return {
		indicadorRisco: riskLabel,
		observacoesRisco: riskNotes || undefined,
		observacoesRiscoTag: recordSession.risk_notes_tag?.trim() || undefined,
	};
};

const mapProcessedClinicalFields = (
	recordSession: MedicalRecords.MedicalRecordSession,
): Partial<ProntuarioPastSessionRecord> => ({
	resumo: recordSession.summary?.trim() || undefined,
	resumoTag: recordSession.summary_tag?.trim() || undefined,
	resumoCurto: recordSession.short_summary?.trim() || undefined,
	resumoCurtoTag: recordSession.short_summary_tag?.trim() || undefined,
	humorObservado: recordSession.observed_mood?.trim() || undefined,
	humorObservadoTag: recordSession.observed_mood_tag?.trim() || undefined,
	queixasSecundarias: recordSession.secondary_complaints?.trim() || undefined,
	queixasSecundariasTag:
		recordSession.secondary_complaints_tag?.trim() || undefined,
	conteudoTecnicas: recordSession.content_and_techniques?.trim() || undefined,
	conteudoTecnicasTag:
		recordSession.content_and_techniques_tag?.trim() || undefined,
	evolucaoObservacoes: recordSession.evolution_notes?.trim() || undefined,
	evolucaoObservacoesTag:
		recordSession.evolution_notes_tag?.trim() || undefined,
	presenca: mapAttendance(recordSession.attendance),
	tasks: mapMedicalRecordSessionTasks(recordSession.tasks),
	...mapRiskFields(recordSession),
});

export const mapMedicalRecordSessionToPastSessionRecord = (
	recordSession: MedicalRecords.MedicalRecordSession,
	selectedDate?: string | Date | null,
): ProntuarioPastSessionRecord => {
	const base: ProntuarioPastSessionRecord = {
		id: recordSession.id,
		sessaoNumero: String(recordSession.session_number),
		data: formatProntuarioSessionDate(
			selectedDate ?? recordSession.created_at,
		),
	};

	if (
		isMedicalRecordSessionPostProcessingPending(
			recordSession.pos_process_status,
		)
	) {
		return base;
	}

	return {
		...base,
		...mapProcessedClinicalFields(recordSession),
	};
};
